<script lang="ts">
  import { onMount } from "svelte";
  import PageContainer from "../components/shared/PageContainer.svelte";
  import Button from "../components/shared/Button.svelte";

  type ViewMode = "day" | "month" | "year";

  interface Statistics {
    totalOpportunities: number;
    newOpportunitiesCreated: number;
    changedOpportunitiesUnique: number;
    changedOpportunitiesTotalUpdates: number;
    successfulSyncs: number;
    failedSyncs: number;
    fromAwsSyncs: number;
    toAwsSyncs: number;
  }

  interface DayStatistics {
    period: string;
    newOpportunitiesCreated: number;
    changedOpportunitiesUnique: number;
  }

  let viewMode: ViewMode = "month";
  let selectedYear: number = new Date().getFullYear();
  let selectedMonth: number = new Date().getMonth() + 1;
  let selectedDay: number = new Date().getDate();
  let statistics: Statistics | null = null;
  let rangeStatistics: DayStatistics[] = [];
  let loading: boolean = false;
  let error: string | null = null;
  let sendingToReporting: boolean = false;

  const API_BASE: string = `${process.env.SVELTE_APP_REMOTE_URL}/api`;
  type StatisticsKey = keyof Statistics;

  const summaryMetrics: Array<{
    key: StatisticsKey;
    label: string;
    valueClass?: string;
  }> = [
    { key: "totalOpportunities", label: "Total Opportunities" },
    { key: "newOpportunitiesCreated", label: "New Opportunities" },
    {
      key: "changedOpportunitiesUnique",
      label: "Changed Opportunities (Unique)",
    },
    {
      key: "changedOpportunitiesTotalUpdates",
      label: "Changed Opportunities (Total Updates)",
    },
    { key: "successfulSyncs", label: "Successful Syncs" },
    { key: "failedSyncs", label: "Failed Syncs", valueClass: "text-red-400" },
    { key: "fromAwsSyncs", label: "From AWS" },
    { key: "toAwsSyncs", label: "To AWS" },
  ];

  const loadStatistics = async (): Promise<void> => {
    loading = true;
    error = null;

    try {
      let url: string;
      if (viewMode === "day") {
        url = `${API_BASE}/sync-statistics/day/${selectedYear}/${selectedMonth}/${selectedDay}`;
      } else if (viewMode === "month") {
        url = `${API_BASE}/sync-statistics/month/${selectedYear}/${selectedMonth}`;
      } else {
        url = `${API_BASE}/sync-statistics/year/${selectedYear}`;
      }

      const response: Response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      statistics = await response.json();
    } catch (err: any) {
      error = err.message;
      statistics = null;
    } finally {
      loading = false;
    }
  };

  const loadRangeStatistics = async (): Promise<void> => {
    if (viewMode !== "month") return;

    loading = true;
    error = null;

    try {
      const startDate: string = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
      const lastDay: number = new Date(
        selectedYear,
        selectedMonth,
        0
      ).getDate();
      const endDate: string = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

      const url: string = `${API_BASE}/sync-statistics/range?startDate=${startDate}&endDate=${endDate}&groupBy=day`;
      const response: Response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      rangeStatistics = (await response.json()) as DayStatistics[];
    } catch (err: any) {
      error = err.message;
      rangeStatistics = [];
    } finally {
      loading = false;
    }
  };

  const sendToReporting = async (): Promise<void> => {
    sendingToReporting = true;
    try {
      await fetch(`${API_BASE}/cron/trigger`, { method: "POST" });
      alert("Successfully triggered reporting");
    } catch (err: any) {
      alert(`Failed: ${err.message}`);
    } finally {
      sendingToReporting = false;
    }
  };
  
  const changeViewMode = (mode: ViewMode): void => {
    viewMode = mode;
    statistics = null;
    rangeStatistics = [];
  };

  const getDayStat = (stat: unknown): DayStatistics => {
    return stat as DayStatistics;
  };

  // Calculate the maximum value for scaling the chart
  $: maxDailyValue =
    rangeStatistics.length > 0
      ? Math.max(
          ...rangeStatistics.map(
            (stat) =>
              getDayStat(stat).newOpportunitiesCreated +
              getDayStat(stat).changedOpportunitiesUnique
          )
        )
      : 1;

  onMount((): void => {
    loadStatistics();
    if (viewMode === "month") {
      loadRangeStatistics();
    }
  });

  $: if (viewMode || selectedYear || selectedMonth || selectedDay) {
    loadStatistics();
    if (viewMode === "month") {
      loadRangeStatistics();
    }
  }
</script>

<PageContainer>
  <div class="stats-page grid w-full min-w-0 gap-3">
    <!-- Header Section -->
    <div class="uimodule">
      <h1 class="text-2xl font-bold text-white mb-1">
        Synchronization Statistics
      </h1>
      <p class="text-sm text-secondary-400">
        Track opportunity synchronization events and performance metrics
      </p>
    </div>

    <!-- Controls -->
    <div class="uimodule">
      <div class="mb-4 flex gap-2">
        <button
          class="px-3 py-2 text-sm rounded {viewMode === 'day'
            ? 'bg-primary-600 text-white font-medium'
            : 'bg-secondary-800 border border-secondary-600 text-secondary-300 hover:bg-secondary-700 hover:text-white'}"
          on:click={() => changeViewMode("day")}
        >
          Daily
        </button>
        <button
          class="px-3 py-2 text-sm rounded {viewMode === 'month'
            ? 'bg-primary-600 text-white font-medium'
            : 'bg-secondary-800 border border-secondary-600 text-secondary-300 hover:bg-secondary-700 hover:text-white'}"
          on:click={() => changeViewMode("month")}
        >
          Monthly
        </button>
        <button
          class="px-3 py-2 text-sm rounded {viewMode === 'year'
            ? 'bg-primary-600 text-white font-medium'
            : 'bg-secondary-800 border border-secondary-600 text-secondary-300 hover:bg-secondary-700 hover:text-white'}"
          on:click={() => changeViewMode("year")}
        >
          Yearly
        </button>
      </div>

      <div class="flex gap-3 items-end flex-wrap">
        <div>
          <label
            for="year-input"
            class="block text-xs font-medium text-secondary-400 mb-1"
            >Year</label
          >
          <input
            id="year-input"
            type="number"
            bind:value={selectedYear}
            min="2020"
            max="2099"
            class="px-3 py-2 bg-secondary-800 border border-secondary-600 text-white rounded text-sm w-24 focus:outline-none focus:border-primary-500"
          />
        </div>

        {#if viewMode !== "year"}
          <div>
            <label
              for="month-input"
              class="block text-xs font-medium text-secondary-400 mb-1"
              >Month</label
            >
            <input
              id="month-input"
              type="number"
              bind:value={selectedMonth}
              min="1"
              max="12"
              class="px-3 py-2 bg-secondary-800 border border-secondary-600 text-white rounded text-sm w-20 focus:outline-none focus:border-primary-500"
            />
          </div>
        {/if}

        {#if viewMode === "day"}
          <div>
            <label
              for="day-input"
              class="block text-xs font-medium text-secondary-400 mb-1"
              >Day</label
            >
            <input
              id="day-input"
              type="number"
              bind:value={selectedDay}
              min="1"
              max="31"
              class="px-3 py-2 bg-secondary-800 border border-secondary-600 text-white rounded text-sm w-20 focus:outline-none focus:border-primary-500"
            />
          </div>
        {/if}

        <Button on:click={loadStatistics} disabled={loading}>
          {loading ? "Loading..." : "Load"}
        </Button>

        <Button variant="secondary" on:click={sendToReporting} disabled={!statistics || sendingToReporting}>
          {sendingToReporting ? "Sending..." : "Send to Reporting"}
        </Button>
      </div>

      {#if error}
        <div
          class="mt-3 p-3 bg-red-900 border border-red-700 rounded text-red-200 text-sm"
        >
          Error: {error}
        </div>
      {/if}
    </div>

    {#if loading}
      <div class="uimodule text-center py-12">
        <div
          class="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-secondary-400"
        ></div>
      </div>
    {:else if statistics}
      <!-- Statistics Cards -->
      <div class="grid w-full grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {#each summaryMetrics as metric (metric.key)}
          <div class="w-full rounded-xl border border-secondary-700 bg-secondary-800 p-4">
            <div class="text-xs font-medium text-secondary-400 mb-1">
              {metric.label}
            </div>
            <div
              class="text-2xl font-bold {metric.valueClass ||
                'text-white'}"
            >
              {statistics[metric.key]}
            </div>
          </div>
        {/each}
      </div>

      <!-- Monthly Range Chart -->
      {#if viewMode === "month" && rangeStatistics.length > 0}
        <div class="uimodule w-full min-w-0">
          <h2 class="text-lg font-bold text-white mb-3">
            Daily Breakdown
          </h2>
          <div class="space-y-2">
            {#each rangeStatistics as dayStat}
              <div class="flex min-w-0 items-center gap-3">
                <div class="w-16 text-xs font-medium text-secondary-400">
                  {getDayStat(dayStat).period.split("-")[2]}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex gap-1">
                    {#if getDayStat(dayStat).newOpportunitiesCreated > 0}
                      <div
                        class="bg-primary-500 h-6 rounded flex items-center justify-center text-white text-xs px-2"
                        style="width: {Math.max(
                          (getDayStat(dayStat).newOpportunitiesCreated /
                            maxDailyValue) *
                            100,
                          5
                        )}%"
                        title="New: {getDayStat(dayStat)
                          .newOpportunitiesCreated}"
                      >
                        {getDayStat(dayStat).newOpportunitiesCreated}
                      </div>
                    {/if}
                    {#if getDayStat(dayStat).changedOpportunitiesUnique > 0}
                      <div
                        class="bg-primary-800 h-6 rounded flex items-center justify-center text-white text-xs px-2"
                        style="width: {Math.max(
                          (getDayStat(dayStat).changedOpportunitiesUnique /
                            maxDailyValue) *
                            100,
                          5
                        )}%"
                        title="Updated: {getDayStat(dayStat)
                          .changedOpportunitiesUnique}"
                      >
                        {getDayStat(dayStat).changedOpportunitiesUnique}
                      </div>
                    {/if}
                  </div>
                </div>
                <div class="w-24 text-xs text-secondary-400">
                  Total: {getDayStat(dayStat).newOpportunitiesCreated +
                    getDayStat(dayStat).changedOpportunitiesUnique}
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>
</PageContainer>

<style>
  .stats-page :global(.bg-secondary-800) {
    background-color: #262a3d !important;
  }
  .stats-page :global(.bg-secondary-900) {
    background-color: #1f2334 !important;
  }
  .stats-page :global(.hover\:bg-secondary-700:hover) {
    background-color: #343a52 !important;
  }
  .stats-page :global(.border-secondary-700) {
    border-color: #3a4059 !important;
  }
  .stats-page :global(.border-secondary-600) {
    border-color: #454b66 !important;
  }
</style>
